import React, { useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { RootStoreContext } from 'index';
import Spinner from 'components/common/Spinner/Spinner';
import { ApplicationRoute } from 'routes/routes.enums';
import { useTimeoutRedirect } from 'hooks/useTimeoutRedirect';
import Button from 'components/common/Button/Button';
import SideBar from 'components/common/SideBar/SideBar';

import styles from './RegistrationProcess.module.scss';
import {
  PrequalificationStepType,
  PrequalificationStep,
  PrequalificationEntrySource,
} from './registrationProcess.enums';
import {
  HALF_HOUR_TIMER,
  INVITE_TO_PORTAL_SENT_SIDE_BAR_SHOWED_SESSION_STORAGE_KEY,
  ONE_HOUR_TIMER,
  REGISTRATION_STEPS,
} from './registrationProcess.constants';
import BusinessInformation from './RegistrationSteps/BusinessInformation';
import AddressInformation from './RegistrationSteps/AddressInformation';
import BusinessMetrics from './RegistrationSteps/BusinessMetrics';
import OwnersInformation from './RegistrationSteps/Owners';

interface RegistrationProcessProps {
  entrySource: PrequalificationEntrySource;
}

const RegistrationProcess = ({ entrySource }: RegistrationProcessProps) => {
  const navigate = useNavigate();
  const [sessionStartTimestamp, setSessionStartTimestamp] = useState<number | null>(null);
  const [isSideBarOpen, setIsSideBarOpen] = useState<boolean>(false);

  const { prequalificationStore, intermediaryApplicationsStore } = useContext(RootStoreContext);
  const { companyProfile } = intermediaryApplicationsStore;
  const {
    intermediaryApplicationId,
    getPrequalificationInformation,
    isLoading,
    getCompletedPrequalificationSteps,
    completedSteps,
    currentStep,
    setCurrentStep,
    lastOpenedStep,
    prequalificationInformation,
    getRegistrationSessionStartTimestamp,
    prequalificationEntrySource,
    setPrequalificationEntrySource,
    setIntermediaryApplicationId,
  } = prequalificationStore;

  useEffect(() => {
    setPrequalificationEntrySource(entrySource);

    const inviteToPortalSentSideBarShowed = sessionStorage.getItem(
      INVITE_TO_PORTAL_SENT_SIDE_BAR_SHOWED_SESSION_STORAGE_KEY,
    );

    if (entrySource === PrequalificationEntrySource.Registration && !inviteToPortalSentSideBarShowed) {
      setIsSideBarOpen(true);

      sessionStorage.setItem(INVITE_TO_PORTAL_SENT_SIDE_BAR_SHOWED_SESSION_STORAGE_KEY, 'true');
    }
  }, []);

  useEffect(() => {
    if (!intermediaryApplicationId && !!companyProfile?.id) {
      setIntermediaryApplicationId(companyProfile?.id);
    }

    const applicationId = intermediaryApplicationId || companyProfile?.id;

    const getInitData = async () => {
      if (!applicationId) {
        navigate(ApplicationRoute.Prequalification);
      } else {
        if (prequalificationEntrySource === PrequalificationEntrySource.Registration) {
          const response = await getRegistrationSessionStartTimestamp(applicationId!);

          setSessionStartTimestamp(+response.timestamp);

          if (Date.now() - parseInt(response.timestamp) >= ONE_HOUR_TIMER) {
            navigate(ApplicationRoute.PrequalificationExpiredSession);

            return;
          }
        }

        await getPrequalificationInformation(applicationId!);
        await getCompletedPrequalificationSteps(applicationId!);
      }
    };

    getInitData();
  }, []);

  useEffect(() => {
    if (
      prequalificationEntrySource === PrequalificationEntrySource.Registration &&
      !!prequalificationInformation?.registrationInformationPartialComplete
    ) {
      navigate(ApplicationRoute.PrequalificationThankYou);
    }

    if (
      prequalificationEntrySource === PrequalificationEntrySource.Portal &&
      !!prequalificationInformation?.registrationInformationFullComplete
    ) {
      navigate(ApplicationRoute.AccountStatus);
    }
  }, [
    prequalificationInformation?.registrationInformationPartialComplete,
    prequalificationInformation?.registrationInformationFullComplete,
  ]);

  useTimeoutRedirect(
    ONE_HOUR_TIMER,
    ApplicationRoute.PrequalificationExpiredSession,
    prequalificationEntrySource === PrequalificationEntrySource.Registration ? sessionStartTimestamp : null,
  );
  useTimeoutRedirect(
    HALF_HOUR_TIMER,
    ApplicationRoute.PrequalificationExpiredSession,
    prequalificationEntrySource === PrequalificationEntrySource.Registration ? Date.now() : null,
    ['keydown', 'mousedown'],
  );

  const handleSideBarClose = () => {
    setIsSideBarOpen(false);
  };

  const getStepContainer = (step: PrequalificationStep, index: number) => {
    if (!completedSteps) {
      return;
    }

    switch (true) {
      case completedSteps[step] === PrequalificationStepType.Complete: {
        return (
          <div
            key={step}
            className={clsx(
              styles.step,
              styles.completedStep,
              styles.clickableStep,
              step === currentStep && styles.currentStep,
            )}
            onClick={() => setCurrentStep(step)}
          >
            <div className={styles.stepNumber}>
              <div className={styles.stepNumberContainer}>
                Step {index + 1}.&nbsp;<div className={styles.completeStepLabel}>Complete</div>
              </div>
            </div>
            <div className={clsx(styles.stepName, styles.completedStepName)}>{step}</div>
          </div>
        );
      }
      case completedSteps[step] === PrequalificationStepType.Incomplete || lastOpenedStep === step: {
        return (
          <div
            key={step}
            className={clsx(styles.step, step === currentStep && styles.currentStep, styles.clickableStep)}
            onClick={() => setCurrentStep(step)}
          >
            <div className={styles.stepNumber}>
              <div className={styles.stepNumberContainer}>
                Step {index + 1}.&nbsp;<div className={styles.incompleteStepLabel}>Incomplete</div>
              </div>
            </div>
            <div className={styles.stepName}>{step}</div>
          </div>
        );
      }
      default: {
        return (
          <div key={step} className={clsx(styles.step, step === currentStep && styles.currentStep)}>
            <div className={styles.stepNumber}>Step {index + 1}.</div>
            <div className={styles.stepName}>{step}</div>
          </div>
        );
      }
    }
  };

  const getCurrentStepContent = (step: PrequalificationStep) => {
    switch (step) {
      case PrequalificationStep.BusinessInformation: {
        return <BusinessInformation />;
      }
      case PrequalificationStep.AddressInformation: {
        return <AddressInformation />;
      }
      case PrequalificationStep.BusinessMetrics: {
        return <BusinessMetrics />;
      }
      case PrequalificationStep.Owners: {
        return <OwnersInformation />;
      }
    }
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <div className={styles.registrationContainer}>
      <div className={styles.registrationMainHeader}>Contractor Application</div>
      <div className={styles.mainContainer}>
        <div className={styles.stepper}>{REGISTRATION_STEPS.map(getStepContainer)}</div>
        <Divider className={styles.divider} />
        <div className={styles.mainContent}>{getCurrentStepContent(currentStep!)}</div>
      </div>
      <SideBar
        title="Invite to Portal Sent!"
        isOpen={isSideBarOpen}
        enableBackdropClick={false}
        onClose={handleSideBarClose}
      >
        <div className={styles.sideBarText}>
          We have emailed you an invite to the Contractor Portal, where you may log in to complete this registration
          later if you wish.
        </div>
        <Button className={styles.sideBarButton} onClick={handleSideBarClose}>
          Got It
        </Button>
      </SideBar>
    </div>
  );
};

export default observer(RegistrationProcess);
